/**
 * Compte de versement du transporteur (Stripe Connect Express) — ce que l'écran dit de chaque état.
 * L'état vient du serveur (`GET /payouts/account`), qui le relit dans Stripe : l'écran ne le déduit jamais.
 *
 *   NONE        pas de compte          → choisir le pays, « Configurer mes versements »
 *   PENDING     inscription commencée  → « Reprendre la configuration »
 *   IN_REVIEW   Stripe vérifie         → rien à faire, attendre
 *   RESTRICTED  information demandée   → « Compléter mes informations »
 *   READY       prêt                   → « Ouvrir mon tableau de bord Stripe »
 */

const VIEWS = {
  NONE: {
    tone: 'off',
    title: 'Versements non configurés',
    text: 'Indiquez le compte bancaire sur lequel recevoir vos gains. L\'inscription se fait chez Stripe, notre prestataire de paiement, et prend quelques minutes.',
    action: 'onboarding',
    actionLabel: 'Configurer mes versements',
  },
  PENDING: {
    tone: 'warn',
    title: 'Configuration à terminer',
    text: 'Votre inscription chez Stripe n\'est pas terminée : vos gains ne peuvent pas encore vous être versés.',
    action: 'onboarding',
    actionLabel: 'Reprendre la configuration',
  },
  IN_REVIEW: {
    tone: 'off',
    title: 'Vérification en cours',
    text: 'Stripe vérifie vos informations. Vous n\'avez rien à faire : vous serez prévenu dès que votre compte sera prêt.',
    action: null,
    actionLabel: null,
  },
  RESTRICTED: {
    tone: 'warn',
    title: 'Information demandée',
    text: 'Stripe a besoin d\'une information ou d\'un document pour activer vos versements.',
    action: 'onboarding',
    actionLabel: 'Compléter mes informations',
  },
  READY: {
    tone: 'pro',
    title: 'Compte de versement prêt',
    text: 'Vos gains pourront vous être versés sur votre compte bancaire.',
    action: 'dashboard',
    actionLabel: 'Ouvrir mon tableau de bord Stripe',
  },
};

/** Ce que l'écran affiche pour un état : { tone, title, text, action: 'onboarding' | 'dashboard' | null, actionLabel }. */
export function payoutView(status) {
  return VIEWS[status] || VIEWS.NONE;
}

/** Nom d'un pays en français d'après son code ISO (« BE » → « Belgique ») ; le code si le navigateur ne sait pas. */
export function countryName(code) {
  try {
    return new Intl.DisplayNames(['fr'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
}

/** Pays couverts, triés par nom français : [{ code, name }]. */
export function countryOptions(codes) {
  return (codes || [])
    .map((code) => ({ code, name: countryName(code) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}

/** Le pays du compte bancaire ne se choisit qu'à la création du compte : ensuite il est fixé chez Stripe. */
export function needsCountry(account) {
  return account?.status === 'NONE';
}

/**
 * Net du transporteur en euros, figé à l'encaissement (B11, étape 2) — ligne affichée sur la mission.
 * `settlement` vient du serveur (`mission.settlement`, vue du transporteur) ; null tant que rien n'est encaissé, et pour
 * une mission en euros (le net est déjà le montant versé). « ≈ » tant que Stripe n'a pas été relu (estimation).
 */
export function euroNetLine(settlement, missionCurrency) {
  if (!settlement || settlement.carrier_net_eur == null || (missionCurrency || 'EUR') === 'EUR') return null;
  const amount = Number(settlement.carrier_net_eur).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const exact = settlement.source === 'stripe';
  return {
    exact,
    label: exact ? `soit ${amount} € versés en euros` : `soit ≈ ${amount} € versés en euros`,
    note: exact
      ? 'Montant figé à l\'encaissement, au taux appliqué par Stripe.'
      : 'Estimation au taux du jour : le montant exact est figé dès que Stripe confirme l\'encaissement.',
  };
}

/**
 * État du versement du net au transporteur (B11, étape 3) — ligne affichée sur la mission, côté transporteur.
 * `transfer` vient du serveur (`mission.transfer`, vue du transporteur) ; null tant que la mission n'est pas terminée.
 * Retourne { tone: 'pro' | 'warn' | 'off', label, link } ou null.
 */
export function transferLine(transfer) {
  if (!transfer || !transfer.status) return null;
  const day = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '');
  const amount = transfer.amount_eur != null
    ? `${Number(transfer.amount_eur).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € `
    : '';
  switch (transfer.status) {
    case 'SCHEDULED':
      return { tone: 'off', label: `Versement prévu le ${day(transfer.due_at)}, à la fin du délai de recours de 48 h.`, link: null };
    case 'SENDING':
      return { tone: 'off', label: 'Versement en cours.', link: null };
    case 'WAITING_ACCOUNT':
      return { tone: 'warn', label: `${amount}vous attendent : configurez vos versements pour les recevoir.`, link: '/app/versements' };
    case 'SENT':
      return { tone: 'pro', label: `${amount}versés le ${day(transfer.sent_at)} sur votre compte de versement.`, link: null };
    case 'SIMULATED':
      return { tone: 'off', label: 'Versement simulé (paiement de démonstration, aucun argent réel).', link: null };
    case 'FAILED':
      return { tone: 'warn', label: 'Versement retardé : l\'équipe Colis2Ship s\'en occupe.', link: null };
    case 'CANCELLED':
      return { tone: 'off', label: 'Pas de versement : le litige a été tranché en faveur de l\'expéditeur.', link: null };
    default:
      return null;
  }
}
